import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useAppUpdate, isStandalone } from "./useAppUpdate";

/**
 * "A new version is available - Reload", for screens that render outside
 * MainLayout and so have no header to hang the refresh button on.
 *
 * The login screen is the one that genuinely needs this: an installed app lands
 * there whenever a session expires, it is where the app is most likely to sit
 * untouched for days, and there is no address bar to reload from. A stale
 * bundle there is a dead end rather than an inconvenience.
 *
 * The manual button appears even when no update was detected, because on this
 * screen the detector is the only other way out and it goes quiet when offline.
 */
const UpdateBanner: React.FC = () => {
  const { t } = useTranslation();
  const { updateReady, reload } = useAppUpdate();
  const [standalone] = React.useState(isStandalone);

  if (updateReady) {
    return (
      <div className="sticky top-0 z-40 bg-teal-600 text-white px-4 py-2 flex items-center justify-between gap-3 text-sm">
        <span>{t("common.updateAvailable")}</span>
        <button
          onClick={reload}
          className="shrink-0 px-3 py-1 bg-white text-teal-700 rounded font-semibold hover:bg-teal-50 transition"
        >
          {t("common.reload")}
        </button>
      </div>
    );
  }

  if (!standalone) return null;

  return (
    <div className="flex justify-end px-4 pt-3">
      <button
        onClick={reload}
        aria-label={t("common.refresh")}
        title={t("common.refresh")}
        className="text-gray-400 hover:text-gray-600 p-1.5 rounded hover:bg-gray-100 transition"
      >
        <ArrowPathIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

export default UpdateBanner;
